<template>
  <page-container :title="title">
    <v-row class="ma-0 pa-2 tab-header" align-content="center">
      <v-tabs v-model="selectedTab" height="36">
        <v-tab :to="{ name: 'profile-personal' }">Personal</v-tab>
        <v-tab :to="{ name: 'profile-employment' }">Employment</v-tab>
        <v-tab :to="{ name: 'profile-children' }">Children</v-tab>
        <v-tab :to="{ name: 'profile-medical' }">Medical</v-tab>
        <v-tab :to="{ name: 'profile-emergency-contact' }"
          >Emergency contact</v-tab
        >
        <v-spacer></v-spacer>
        <transition-group name="fade">
          <v-btn
            class="button mx-2 background-color--inactive color--foreground"
            elevation="0"
            tile
            v-show="edited"
            key="cancel-btn"
            style="animation-duration: 0.3s"
            @click="clearData"
            :disabled="saving"
            >Cancel</v-btn
          >

          <v-btn
            color="primary"
            class="button mx-2"
            elevation="0"
            tile
            v-show="edited"
            key="save-btn"
            style="animation-duration: 0.3s"
            @click="saveData"
            :loading="saving"
            >Save</v-btn
          >
        </transition-group>
      </v-tabs>
    </v-row>
    <transition name="fade">
      <v-progress-linear
        indeterminate
        height="2px"
        v-if="saving"
        style="animation-duration: 0.3s"
      ></v-progress-linear>
    </transition>
    <v-row class="ma-0 pa-0">
      <v-col cols="12" md="3" class="px-0 pt-2 pb-0">
        <v-sheet style="overflow: hidden">
          <v-skeleton-loader
            type="image, list-item-three-line"
            v-if="loading"
          ></v-skeleton-loader>
          <v-row v-else>
            <v-col align="center">
              <user-avatar
                :userInfo="profileInfo"
                width="200px"
                height="200px"
              ></user-avatar>
              <div class="avatar-name ma-1 mt-2">{{ fullName }}</div>
              <div class="text-section-title mb-1">
                {{ jobTitle }}
              </div>
              <div class="text-body-dark mb-1">
                {{ department }}
              </div>
            </v-col>
          </v-row>
        </v-sheet>
      </v-col>
      <v-col
        cols="12"
        md="9"
        class="px-0 pb-2 pt-2"
        :class="{
          'pl-2': $vuetify.breakpoint.mdAndUp,
          'pt-0': $vuetify.breakpoint.smAndDown,
        }"
      >
        <v-form :disabled="saving">
          <router-view
            :profileData="editedProfileInfo"
            :restrictedProfileData="editedRestrictedData"
          ></router-view>
        </v-form>
      </v-col>
    </v-row>
  </page-container>
</template>

<script>
import PageContainer from '@/components/PageContainer.vue'
import { mapGetters, mapActions } from 'vuex'
import { db, functions } from '@/firebaseConfig'
import { objects, deepEqual, goclone } from '@/helpers'
import UserAvatar from '@/components/UserAvatar.vue'

const emptyObject = {
  personal: { children: [] },
  employment: {},
  bank: {},
  address: {},
  holidays: {},
  medical: { address: {}, conditions: [] },
  emergency: {},
}

export default {
  name: 'ProfileView',

  props: ['id'],
  components: { PageContainer, UserAvatar },
  data: () => ({
    loading: false,
    saving: false,
    formValid: false,
    selectedTab: 0,
    profileInfo: null,
    editedProfileInfo: null,
    restrictedData: null,
    editedRestrictedData: null,
  }),
  mounted() {
    this.loadData()
  },

  async beforeRouteLeave(to, from, next) {
    if (this.edited) {
      const save = await this.$confirm(
        'Are you sure you want to leave? All usaved changes will be lost.',
        {
          title: 'Usaved changes',
          confirmBtnText: 'Save changes',
          dismissBtnText: 'Discard changes',
        }
      )
      if (save) {
        const error = await this.saveData()
        if (!error) {
          next()
        }
      } else {
        next()
      }
    } else {
      next()
    }
  },

  async beforeRouteUpdate(to, from, next) {
    if (to.params.id !== from.params.id) {
      await this.loadData(to.params.id)
    }
    next()
  },
  computed: {
    ...mapGetters(['companyId']),
    title() {
      if (!this.profileInfo) {
        return undefined
      } else {
        return [
          {
            to: 'workforce',
            text: 'People',
          },
          {
            text: [
              this.profileInfo?.firstName ?? '',
              this.profileInfo?.lastName ?? '',
            ].join(' '),
          },
        ]
      }
    },
    jobTitle() {
      return this.profileInfo?.jobTitle ?? ''
    },
    department() {
      return ''
    },
    ref() {
      return this.userDocRef(this.id)
    },
    collectionRef() {
      return db.collection(`companies/${this.companyId}/workforce`)
    },
    restrictedRef() {
      return this.ref.collection('restricted')
    },

    openProfileDataChanged() {
      return !deepEqual(this.editedProfileInfo, this.profileInfo)
    },
    restrictedProfileDataChanged() {
      return !deepEqual(this.editedRestrictedData, this.restrictedData)
    },
    edited() {
      return this.openProfileDataChanged || this.restrictedProfileDataChanged
    },

    fullName() {
      return [
        this.profileInfo?.firstName ?? '',
        this.profileInfo?.lastName ?? '',
      ].join(' ')
    },
  },
  methods: {
    ...mapActions(['showError', 'showSuccess']),
    userDocRef(userId) {
      return this.collectionRef.doc(userId)
    },
    async loadData(userId = this.id) {
      try {
        this.loading = true
        const profileDataSnapshot = await this.userDocRef(userId).get()
        let publicData = profileDataSnapshot.data()
        for (let key of ['invite', 'keywords']) {
          delete publicData[key]
        }

        if (publicData.roles) {
          publicData.roles = publicData.roles.map((r) => r.path)
        }

        this.profileInfo = Object.assign({}, goclone(publicData))
        this.editedProfileInfo = Object.assign({}, goclone(publicData))

        const restrictedDataSnapshot = await this.userDocRef(userId)
          .collection('restricted')
          .get()
        let data = {}

        restrictedDataSnapshot.forEach((doc) => {
          data[doc.id] = doc.data()
        })

        this.restrictedData = Object.assign(goclone(emptyObject), goclone(data))
        this.editedRestrictedData = Object.assign(
          goclone(emptyObject),
          goclone(data)
        )

        if (!this.editedRestrictedData.personal.children) {
          this.editedRestrictedData.personal.children = []
        }
      } catch (error) {
        this.showError(
          '<b>Failed to load user profile</b>\n' + error.message ??
            'Please try again later or contact support'
        )
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    async saveData() {
      let returnVal
      try {
        this.saving = true

        const updateWorkforceFunc = functions.httpsCallable(
          'people-updateWorkforce'
        )

        const data = Object.assign({}, this.editedProfileInfo)
        data.uid = this.id
        data.companyId = this.companyId

        console.log('data changed: ', this.openProfileDataChanged)
        if (this.openProfileDataChanged) {
          await updateWorkforceFunc(data)
        }

        this.profileInfo = Object.assign({}, this.editedProfileInfo)

        const {
          personal,
          bank,
          employment,
          address,
          holidays,
          medical,
          emergency,
        } = this.editedRestrictedData

        const batch = db.batch()
        batch.set(
          this.restrictedRef.doc('personal'),
          objects.withUpdatedDataInfo(personal),
          { merge: true }
        )
        batch.set(
          this.restrictedRef.doc('bank'),
          objects.withUpdatedDataInfo(bank),
          { merge: true }
        )
        batch.set(
          this.restrictedRef.doc('employment'),
          objects.withUpdatedDataInfo(employment),
          { merge: true }
        )
        batch.set(
          this.restrictedRef.doc('address'),
          objects.withUpdatedDataInfo(address),
          { merge: true }
        )
        batch.set(
          this.restrictedRef.doc('holidays'),
          objects.withUpdatedDataInfo(holidays),
          { merge: true }
        )
        batch.set(
          this.restrictedRef.doc('medical'),
          objects.withUpdatedDataInfo(medical),
          { merge: true }
        )
        batch.set(
          this.restrictedRef.doc('emergency'),
          objects.withUpdatedDataInfo(emergency),
          { merge: true }
        )
        await batch.commit()

        for (let key in this.editedRestrictedData) {
          this.restrictedData[key] = Object.assign(
            {},
            this.editedRestrictedData[key]
          )
        }

        this.showSuccess(
          '<b>Saved</b>\nUser data has been successfully updated'
        )
      } catch (error) {
        console.error(error)
        this.showError(
          '<b>Failed to save</b>\n' + error.message ??
            'Please try again later or contact support'
        )

        returnVal = error
      } finally {
        this.saving = false
      }

      return returnVal
    },
    async clearData() {
      const confirmed = await this.$confirm(
        'Are you sure you want to discard all changes?',
        { title: 'Please confirm', confirmBtnText: 'Confirm' }
      )
      if (confirmed) {
        this.editedRestrictedData = Object.assign(
          goclone(emptyObject),
          goclone(this.restrictedData)
        )

        this.editedProfileInfo = Object.assign({}, goclone(this.profileInfo))
      }
    },
  },
}
</script>